import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Loader2, Eye, EyeOff, UserPlus, Lock } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabaseClient';
import { LanguageSelector } from '../../components/ui/language-selector';
import { authLogger } from '@/lib/logging';
import {
  changeLanguage,
  initializeLanguage,
  getLanguageFromURL,
  type SupportedLanguage
} from '../../lib/language-service';

// Simplified invitation page - no session management, just show the form
// Security is handled server-side by the complete-invitation edge function
const InvitationPageContent: React.FC<{
  token: string | undefined;
  searchParams: URLSearchParams;
}> = ({ token, searchParams }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, i18n } = useTranslation();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [shakeError, setShakeError] = useState(false);
  const [languageLoading, setLanguageLoading] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguage>('en');
  const [_invitationEmail, setInvitationEmail] = useState<string>('');

  // Initialize language and validate token - safe because session is already verified clean
  useEffect(() => {
    const initializeCleanInvitation = async () => {
      authLogger.warn('[invitation] Initializing with verified clean session');

      // Initialize language from URL parameter (from invitation email) or fallback
      const urlLanguage = getLanguageFromURL() || searchParams.get('lng');
      if (urlLanguage && ['en', 'es', 'fr', 'sv'].includes(urlLanguage)) {
        const langCode = urlLanguage as SupportedLanguage;
        await changeLanguage(langCode, false);
        setSelectedLanguage(langCode);
        // ALWAYS force i18n to apply the URL language immediately
        await i18n.changeLanguage(langCode);
        authLogger.info(`[invitation] Initial language set from URL: ${langCode}`);
      } else {
        initializeLanguage();
        setSelectedLanguage('en');
        await i18n.changeLanguage('en');
        authLogger.info('[invitation] Initial language set to default: en');
      }

      // Validate token format first
      if (!token) {
        setError('Invalid invitation link. No token provided.');
        return;
      } else if (token.length < 32) {
        setError('Invalid invitation token format.');
        return;
      }

      // Now safe to validate token - no session exists to cause pollution
      try {
        const response = await fetch(`${import.meta.env['VITE_PUBLIC_SUPABASE_URL']}/functions/v1/validate-invitation-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env['VITE_PUBLIC_SUPABASE_ANON_KEY']}`
          },
          body: JSON.stringify({ token })
        });

        if (response.ok) {
          const data = await response.json();
          setInvitationEmail(data.userEmail || '');
          authLogger.warn('[invitation] ✅ Token validation successful for email:', data.userEmail);
        } else {
          const errorData = await response.json();
          authLogger.error('[invitation] Token validation failed:', errorData);
          setError(errorData.error || 'Invalid invitation token');
        }
      } catch (_error) {
        authLogger.error('Failed operation:', _error);
        setError('Failed to validate invitation token');
      }
    };

    initializeCleanInvitation();
  }, [token, searchParams, i18n]);

  const handleLanguageChange = async (language: SupportedLanguage) => {
    if (languageLoading) return;

    setLanguageLoading(true);

    try {
      // Don't save to database yet (user not authenticated)
      // Just update cookie and localStorage
      await changeLanguage(language, false);
      setSelectedLanguage(language);

      // ALWAYS force i18n to change language, even if it thinks it's already on that language
      // This fixes the race condition where i18n initializes before our language service updates
      await i18n.changeLanguage(language);

      authLogger.info(`[invitation] Language changed to: ${language}`);
    } catch (_error) {
      authLogger.error('Failed operation:', _error);
    } finally {
      setLanguageLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate inputs
    if (!password || !confirmPassword) {
      setError('Please fill in all fields');
      setShakeError(true);
      setTimeout(() => setShakeError(false), 600);
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth.invitation.passwordsDoNotMatch'));
      setShakeError(true);
      setTimeout(() => setShakeError(false), 600);
      return;
    }

    if (password.length < 6) {
      setError(t('auth.invitation.passwordTooShort'));
      setShakeError(true);
      setTimeout(() => setShakeError(false), 600);
      return;
    }

    setLoading(true);

    try {
      // Call the complete-invitation edge function
      const response = await fetch(`${import.meta.env['VITE_PUBLIC_SUPABASE_URL']}/functions/v1/complete-invitation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env['VITE_PUBLIC_SUPABASE_ANON_KEY']}`,
        },
        body: JSON.stringify({
          token,
          password,
          language: selectedLanguage
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to complete invitation');
      }

      // Check if session was returned for auto-login
      if (data.session) {
        authLogger.warn('[invitation] Auto-login successful, clearing storage and setting clean session');

        // CRITICAL: Clear ALL browser storage to prevent contamination from previous user
        localStorage.clear();
        sessionStorage.clear();

        // Set the new session in clean storage
        supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token
        });

        // Hard reload to root for clean app initialization with new session
        // This ensures React components mount fresh with no stale cache
        authLogger.warn('[invitation] Redirecting to dashboard with clean session');
        window.location.href = '/';
      } else {
        // Fallback: No session returned, go to login page
        authLogger.warn('[invitation] No session returned, redirecting to login');

        toast({
          title: t('auth.invitation.accountActivatedToastTitle'),
          description: 'Please login with your new password.',
        });

        navigate('/login', { replace: true });
      }

    } catch (err: unknown) {
      authLogger.error('Error completing invitation:', err);
      setError(err instanceof Error ? err.message : 'Failed to complete invitation. Please try again.');
      setShakeError(true);
      setTimeout(() => setShakeError(false), 600);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className='flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 transition-colors duration-500' 
      style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        width: '100%',
        position: 'fixed',
        top: 0,
        left: 0
      }}
    >
      <div className='w-full max-w-md'>
        <Card className={`shadow-xl border-0 bg-white/80 backdrop-blur-sm transition-all duration-300 hover:shadow-2xl ${shakeError ? 'animate-shake' : ''}`}>
          <CardHeader className="text-center pb-8">
            <div className='flex justify-end mb-4'>
              <LanguageSelector
                value={selectedLanguage}
                onLanguageChange={handleLanguageChange}
                disabled={languageLoading || loading}
                variant="select"
                size="sm"
                showIcon={true}
                showLabel={false}
              />
            </div>
            <div className="flex items-center justify-center mb-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 shadow-lg">
                <UserPlus className="h-8 w-8 text-primary" />
              </div>
            </div>
            <CardTitle className="text-3xl font-bold text-slate-900">
              {t('auth.invitation.title')}
            </CardTitle>
            <CardDescription className="text-slate-600 mt-2">
              {t('auth.invitation.description')}
            </CardDescription>
          </CardHeader>
        <CardContent className="pb-8">
          <form onSubmit={handlePasswordSubmit} className="space-y-6">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg" role="alert" aria-live="polite">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-slate-700 flex items-center">
                <Lock className="mr-2 h-4 w-4 text-slate-500" />
                {t('auth.invitation.newPasswordLabel')}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (error) setError(null);
                  }}
                  placeholder="••••••••"
                  disabled={loading}
                  required
                  minLength={6}
                  className="h-12 pr-12 transition-all duration-200 focus:ring-2 focus:ring-primary/20 border-slate-200 hover:border-primary/40"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  disabled={loading}
                  aria-label={showPassword ? t('auth.invitation.hidePassword') : t('auth.invitation.showPassword')}
                  tabIndex={0}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm font-medium text-slate-700 flex items-center">
                <Lock className="mr-2 h-4 w-4 text-slate-500" />
                {t('auth.invitation.confirmPasswordLabel')}
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (error) setError(null);
                  }}
                  placeholder="••••••••"
                  disabled={loading}
                  required
                  minLength={6}
                  className="h-12 pr-12 transition-all duration-200 focus:ring-2 focus:ring-primary/20 border-slate-200 hover:border-primary/40"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  disabled={loading}
                  aria-label={showConfirmPassword ? t('auth.invitation.hidePassword') : t('auth.invitation.showPassword')}
                  tabIndex={0}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 text-base font-medium transition-all duration-200 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  {t('auth.invitation.activatingAccount')}
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <UserPlus className="mr-2 h-5 w-5" />
                  {t('auth.invitation.activateAccountButton')}
                </div>
              )}
            </Button>

            <div className="text-center text-sm text-slate-600">
              {t('auth.invitation.alreadyHaveAccount')}{' '}
              <Link to="/login" className="font-medium text-primary hover:text-primary/80 transition-colors duration-200">
                {t('auth.invitation.signIn')}
              </Link>
            </div>
          </form>
        </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Main component - renders invitation form immediately
const InvitationPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();

  return (
    <InvitationPageContent
      token={token}
      searchParams={searchParams}
    />
  );
};

export default InvitationPage;